import { useEffect, useMemo, useState } from 'react'
import Card, { CardBody, CardHeader } from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import { Field, Select } from '../../../components/ui/Field'
import { authApi, referralsApi } from '../../../services/api'

const formatAmount = (amount, currency = 'NGN') => {
  const value = Number(amount || 0)
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(value)
}

const formatDate = (row) => {
  const value = row?.activationPayment?.createdAt || row?.createdAt
  if (!value) return '-'
  const dt = new Date(value)
  return Number.isNaN(dt.getTime()) ? '-' : dt.toLocaleString()
}

const Referrals = () => {
  const [me, setMe] = useState(null)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 })
  const [generatingCode, setGeneratingCode] = useState(false)

  useEffect(() => {
    authApi.me().then((res) => setMe(res?.data?.data || null)).catch(() => setMe(null))
  }, [])

  useEffect(() => {
    let active = true
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const res = await referralsApi.mine({
          page,
          limit,
          status: status || undefined
        })
        if (!active) return
        setItems(res?.data?.data || [])
        setPagination(res?.data?.pagination || { total: 0, page: 1, limit, pages: 1 })
      } catch (e) {
        if (!active) return
        setItems([])
        setError(e?.response?.data?.message || 'Could not load referrals')
      } finally {
        if (active) setLoading(false)
      }
    }
    void load()
    return () => {
      active = false
    }
  }, [page, limit, status])

  const referralCode = (me?.referralCode || '').trim()
  const [copied, setCopied] = useState(false)
  const handleGenerateCode = async () => {
    if (generatingCode) return
    setGeneratingCode(true)
    setError('')
    try {
      const res = await referralsApi.generateCode()
      const code = res?.data?.data?.referralCode || ''
      if (code) setMe((prev) => ({ ...(prev || {}), referralCode: code }))
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not generate referral code')
    } finally {
      setGeneratingCode(false)
    }
  }

  const unpaidCount = useMemo(() => items.filter((i) => i.status === 'unpaid').length, [items])
  const paidCount = useMemo(() => items.filter((i) => i.status === 'paid').length, [items])
  const unpaidTotal = useMemo(() => items.filter((i) => i.status === 'unpaid').reduce((sum, i) => sum + Number(i.amount || 0), 0), [items])

  const isEmpty = !loading && !error && items.length === 0
  const pages = pagination.pages || 1

  return (
    <section className="space-y-5">
      <Card className="shadow-soft overflow-hidden">
        <CardHeader
          title="Referrals"
          subtitle="Earn ₦2,000 when a referred student pays for documentation generation."
          className="bg-gradient-to-r from-emerald-500/10 via-white to-white border-b border-slate-200/70"
        />
        <CardBody className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200/70 bg-white p-4">
              <p className="text-xs font-semibold text-slate-500">Your referral code</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <p className="text-lg font-bold text-slate-900 break-all">{referralCode || '—'}</p>
                {referralCode ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard?.writeText(referralCode).catch(() => {})
                      setCopied(true)
                      setTimeout(() => setCopied(false), 2000)
                    }}
                  >
                    {copied ? 'Copied!' : 'Copy code'}
                  </Button>
                ) : null}
              </div>
              <p className="mt-1 text-xs text-slate-600">Share this code at signup.</p>
              {!referralCode ? (
                <Button className="mt-3" variant="outline" size="sm" onClick={() => void handleGenerateCode()} loading={generatingCode}>
                  Generate code
                </Button>
              ) : null}
            </div>
            <div className="rounded-2xl border border-slate-200/70 bg-white p-4">
              <p className="text-xs font-semibold text-slate-500">Unpaid (this page)</p>
              <p className="mt-2 text-lg font-bold text-slate-900">{unpaidCount}</p>
              <p className="mt-1 text-xs text-slate-600">Total unpaid: {formatAmount(unpaidTotal, 'NGN')}</p>
            </div>
            <div className="rounded-2xl border border-slate-200/70 bg-white p-4">
              <p className="text-xs font-semibold text-slate-500">Paid (this page)</p>
              <p className="mt-2 text-lg font-bold text-slate-900">{paidCount}</p>
              <p className="mt-1 text-xs text-slate-600">Admin marks payouts as paid.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Field label="Status" hint="Filter payouts" className="lg:col-span-1">
              <Select
                value={status}
                onChange={(e) => {
                  setPage(1)
                  setStatus(e.target.value)
                }}
              >
                <option value="">All</option>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
              </Select>
            </Field>
          </div>

          {error ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-800">
              <p className="text-sm font-medium">{error}</p>
            </div>
          ) : null}

          {loading ? (
            <div className="flex justify-center py-10">
              <span className="loading loading-spinner loading-md" />
            </div>
          ) : null}

          {isEmpty ? (
            <div className="flex flex-col items-center justify-center text-center border border-dashed border-slate-300/80 rounded-2xl p-10 bg-slate-50">
              <p className="text-sm font-semibold text-slate-900">No referral payouts yet</p>
              <p className="mt-1 text-sm text-slate-600">When a referred student pays for documentation generation, it will appear here.</p>
            </div>
          ) : null}

          {!loading && !isEmpty ? (
            <div className="space-y-4">
              <div className="overflow-hidden rounded-2xl border border-slate-200/70">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs font-semibold text-slate-600">
                      <th className="px-5 py-3">Referred student</th>
                      <th className="px-5 py-3">Activated</th>
                      <th className="px-5 py-3">Amount</th>
                      <th className="px-5 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((row) => (
                      <tr key={row._id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3 font-medium text-slate-900">
                          {row?.referredStudent?.email || row?.referredStudent?.name || '-'}
                        </td>
                        <td className="px-5 py-3 text-slate-600">{formatDate(row)}</td>
                        <td className="px-5 py-3 text-slate-900 font-semibold">{formatAmount(row?.amount, row?.currency || 'NGN')}</td>
                        <td className="px-5 py-3">
                          <span className={['badge badge-sm', row.status === 'paid' ? 'badge-success' : 'badge-warning'].join(' ')}>
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-slate-200/70 bg-white p-4">
                <p className="text-sm text-slate-600">
                  Page {pagination.page} of {pages}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => setPage((p) => Math.max(p - 1, 1))}>
                    Prev
                  </Button>
                  <Button variant="outline" size="sm" disabled={pagination.page >= pages} onClick={() => setPage((p) => Math.min(p + 1, pages))}>
                    Next
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </section>
  )
}

export default Referrals

